import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {BehaviorSubject} from 'rxjs';

import {User} from '../core/model/user';
import {GetActiveUserAction, SetActiveUser} from '../core/store/active-user/active-user.action';
import {GetAllRolesAction} from '../core/store/roles/roles.action';
import {GetAllUsersAction} from '../core/store/users/users.action';
import {AppThemeService} from './../shared/services/app-theme.service';
import {MessageService} from './message.service';
import {TimezoneCheckerService} from './timezone-checker.service';
import {getLSUserInfo} from '../core/localStorage/ls-user-info';
import {GetCoinTypesAction} from '../core/store/rewards/rewards.action';
import {RewardCoin} from '../core/model/reward';
import {CoinTypes} from '../rewards/shared/rewards-constants';

type ResolveType<T> = (value: T) => void;

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  usersLoaded$ = new BehaviorSubject<boolean>(false);
  rolesLoaded$ = new BehaviorSubject<boolean>(false);
  coinTypesLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(
    private store$: Store,
    private messageService: MessageService,
    private router: Router,
    private appThemeService: AppThemeService,
    private timezoneChecker: TimezoneCheckerService
  ) {}

  initApp(pathname: string) {
    this.appThemeService.load();
    return new Promise(resolve => {
      if (pathname?.startsWith('/reset-password/') || pathname?.startsWith('/user-request/')) {
        return resolve(true);
      }

      const currentUser = getLSUserInfo();
      if (!currentUser?.email) {
        this.router.navigate(['login']);
        return resolve(true);
      }

      this.store$.dispatch(
        new GetActiveUserAction({
          onSuccess: user => this.onGetActiveUserSuccess(user, resolve),
          onFailure: () => {
            this.onGetActiveUserFailure(), resolve(true);
          },
        })
      );
    });
  }

  onGetActiveUserSuccess(activeUser: User, resolve: ResolveType<boolean>) {
    if (!activeUser) {
      this.messageService.add('User session expired.');
      this.router.navigate(['login']);
      return resolve(true);
    }

    this.store$.dispatch(new SetActiveUser({user: activeUser}));
    this.store$.dispatch(
      new GetCoinTypesAction({onSuccess: coinTypes => this.onGetCoinTypesSuccess(activeUser, coinTypes, resolve)})
    );
    this.store$.dispatch(new GetAllRolesAction({onSuccess: () => this.onGetRolesSuccess(resolve)}));
    this.store$.dispatch(new GetAllUsersAction({onSuccess: () => this.onGetUsersSuccess(resolve)}));
  }

  onGetCoinTypesSuccess(activeUser: User, coinTypes: RewardCoin[], resolve: ResolveType<boolean>) {
    const normalizedGoldenName = (CoinTypes.Golden as string).trim().toLowerCase();
    const goldCoinId = coinTypes.find(coin => coin.name?.trim().toLowerCase() === normalizedGoldenName)?._id;
    const systemCoins = activeUser.coins?.find(coin => coin.coinType?.toString() === goldCoinId)?.coinCount ?? 0;

    this.store$.dispatch(new SetActiveUser({user: {...activeUser, systemCoins}}));
    this.coinTypesLoaded$.next(true);
    this.checkIfRolesUsersAndCoinTypesLoaded(resolve);
  }

  onGetUsersSuccess(resolve: ResolveType<boolean>) {
    this.usersLoaded$.next(true);
    this.checkIfRolesUsersAndCoinTypesLoaded(resolve);
  }

  onGetActiveUserFailure() {
    this.router.navigate(['login']);
    // this.messageService.add('There was a problem getting the active user.');
  }

  onGetRolesSuccess(resolve: ResolveType<boolean>) {
    this.rolesLoaded$.next(true);
    this.checkIfRolesUsersAndCoinTypesLoaded(resolve);
  }

  checkIfRolesUsersAndCoinTypesLoaded(resolve: ResolveType<boolean>) {
    if (this.usersLoaded$.value && this.rolesLoaded$.value && this.coinTypesLoaded$.value) {
      // Check timezone after user is loaded and authenticated
      this.timezoneChecker.checkTimezone();
      resolve(true);
    }
  }
}
